import { AgentType } from './Computer';
import { LicenseType } from './LicenseType.enum';

export class AdministratorLicense implements License {
  dateExpired: string;
  id: string;
  isTrial: boolean;
  licenseType: LicenseType;
  licenseDisplayName: string;
  isAutoRenew: boolean;
}

export interface License {
  dateExpired: string;
  id: string;
  isTrial: boolean;
  licenseType: LicenseType;
  isAutoRenew?: boolean;
  licenseDisplayName?: string;
}

export default class LicenseOData {
  Type: LicenseType;
  ExpireDate: Date;
}

export class LicenseTypeSummary {
  type: LicenseType;
  displayName: string;
}

export interface ComputerGrantLicenseResultDTO {
  availableLicenses: LicenseOptionDTO[];
  isAllowBuy: boolean;
  isGranted: boolean;
  license: LicenseInfoDTO | null;
}

export interface LicenseInfoDTO {
  dateActivated: string;
  dateCreated: string;
  dateExpired: string;
  id: string;
  licenseNumber: number;
  licenseType: LicenseType;
}
export interface LicenseOptionDTO {
  count: number;
  licenseType: LicenseType;
}

export interface AvailableLicenses {
  trialLicense: {
    licenseId: string;
    days: number;
  };
  paidLicense: {
    licenseId: string;
    poolName: string;
    validUntil: string;
  };
  newLicensePrice: string;
}

export interface AvailablePaidLicense {
  count: number;
  licenseId: string;
  poolName: string;
  validUntil: string;
  name: string | null;
}

export enum LicenseKind {
  Unknown,
  Backup,
  Apps,
  Rmm,
  Connect,
  Service,
  Storage,
  VmSocket,
  Branding,
  DeepInstinct
}

export function getAgentTypeForLicenseKind(kind: LicenseKind): AgentType | null {
  switch (kind) {
    case LicenseKind.Backup:
      return AgentType.Backup;
    case LicenseKind.Rmm:
      return AgentType.RMM;
    case LicenseKind.Connect:
      return AgentType.RA;
    default:
      return null;
  }
}

export enum LicenseStatus {
  Trial = 0,
  Paid = 1,
  Free = 2
}

export enum ComputerLicenseStatus {
  Trial = 0,
  Paid,
  Free,
  Expired,
  NoLicense
}

export const ComputerLicenseStatusMap: Map<ComputerLicenseStatus, string> = new Map([
  [ComputerLicenseStatus.NoLicense, 'shared:licenses.status.noLicense'],
  [ComputerLicenseStatus.Trial, 'shared:licenses.status.trial'],
  [ComputerLicenseStatus.Paid, 'shared:licenses.status.paid'],
  [ComputerLicenseStatus.Expired, 'shared:licenses.status.expired'],
  [ComputerLicenseStatus.Free, 'shared:licenses.status.free']
]);

export enum ComputerLicenseType {
  Backup,
  RMM,
  Connect
}

export interface ComputerLicense {
  activated: string;
  autoRenew: boolean;
  isExpired: boolean;
  status: LicenseStatus;
  licenseId?: string;
  licenseKind: LicenseKind;
  licenseType: LicenseType;
  validUntil: string;
}

export interface ExtendedComputerLicense extends ComputerLicense {
  licenseUsageId?: string;
  pool?: ComputerLicensePool;
  socketsCount?: number;
  licenseIds?: string[];
  licenseUsageIds?: string[];
}

export interface ComputerSocketLicenseGroup {
  licenseIds: string[];
  licenseUsageIds: string[];
  activated: string;
  validUntil: string;
  autoRenew: boolean;
  isFree: boolean;
  status: LicenseStatus;
  isExpired: boolean;
}

export interface ComputerLicensesResponse {
  licenses: ExtendedComputerLicense[];
  sockets: ComputerSocketLicenseGroup[];
}

export interface ComputerLicensePool {
  name: 'Company pool' | 'User pool' | 'Global pool';
  subject: {
    id: string;
    name: string;
  };
}

export interface AvailableLicenseType {
  id: number;
  isAllowForCompany: boolean;
  isDefault: boolean;
}

export type LicenseAssignToAdmin = { adminId: string; license: AdministratorLicense };
export type FilterLicenseAndAdminId = {
  filter: LicenseType[];
  adminID: string;
  expiredCurrent?: boolean;
  email?: string;
};

export enum SubscriptionLicenseMode {
  None = 0,
  Monthly = 1,
  Yearly = 2,
  CustomPeriod = 3, // for future, not used in the current implementation
  CumulativeV2Monthly = 4,
  CumulativeV2Annual = 5,
  CumulativeV3Monthly = 6,
  CumulativeV3Annual = 7,
  CumulativeV4Monthly = 8,
  CumulativeV4Annual = 9,

  PostPaymentV1Monthly = 1001, // could be only monthly
  ResellerAnnual = 2001,
  ResellerAnnualGold = 2002,
  ResellerAnnualPlatinum = 2003
}
